import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

import SecondaryButton from '../../buttons/SecondaryButton';
import fetch from '../../../../utils/fetcher';
import useStyles from './styles';
import ErrorMessage from '../ErrorMessage';
import { apiUrl } from '../../../../config';
import { uploadVideoToExternalApi, sendVideoUrlToOperations, MAX_VIDEO_DURATION, MAX_FILE_SIZE } from './utils';


const VideoInputComponent = ({
  uploading,
  setUploading,
  setFieldValue,
  setFieldError,
  campaignId,
  id,
  editMode,
}) => {
  const { t } = useTranslation(['translation']);
  const [uploadToken, setUploadToken] = useState('');
  const classes = useStyles();

  useEffect(() => {
    const fetchUploadToken = async () => {
      try {
        const token = await getUploadToken();
        setUploadToken(token);
      } catch (error) {
        console.error(`fetchUploadToken error: `, error);
        setFieldError(t('CAMPAIGN.VIDEO_UPLOADER.VIDEO_UPLOAD_ERROR'));
      }
    };
    fetchUploadToken();
  }, []);

  const getUploadToken = async () => {
    const url = `${apiUrl}/campaign/video/token`;
    try {
      const { token } = await fetch(url).then((res) => res.json());
      return token;
    } catch (error) {
      console.error(`getUploadToken error: `, error);
      setFieldError(t('CAMPAIGN.VIDEO_UPLOADER.VIDEO_UPLOAD_ERROR'));
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isValid = await validateVideo(file);
    if (!isValid) return;

    try {
      setUploading(true);
      const videoId = await uploadVideoToExternalApi(file, uploadToken);
      const videoCode = await sendVideoUrlToOperations(videoId, campaignId);
      setFieldValue(videoCode);
    } catch (error) {
      setFieldError(t('CAMPAIGN.VIDEO_UPLOADER.VIDEO_UPLOAD_ERROR'));
      console.error('Error uploading video:', error);
    } finally {
      setUploading(false);
    }
  };

  const validateVideo = (file) => {
    return new Promise((resolve) => {
      if (file.size > MAX_FILE_SIZE) {
        setFieldError(t('CAMPAIGN.VIDEO_UPLOADER.VIDEO_SIZE_TOO_LARGE', { size: MAX_FILE_SIZE / (1024 * 1024) }));
        resolve(false);
        return;
      }
  
      const videoElement = document.createElement('video');
      videoElement.src = URL.createObjectURL(file);
  
      videoElement.onloadedmetadata = () => {
        if (videoElement.duration > MAX_VIDEO_DURATION) {
          setFieldError(t('CAMPAIGN.VIDEO_UPLOADER.VIDEO_TIME_TOO_LONG', { time: MAX_VIDEO_DURATION }));
          resolve(false);
        } else {
          setFieldError('');
          resolve(true);
        }
      };
    });
  };

  return (
    <div className={classes.wrapper}>
      <p className={classes.videoLinkTitle}>{t('CAMPAIGN.VIDEO_UPLOADER.TITLE')}</p>
      <div className={classes.shortContainer}>
        <ErrorMessage id={id} />
        <input
          accept="video/*"
          className={classes.hiddenInput}
          id="video-file-input"
          type="file"
          onChange={handleFileChange}
        />
        {!uploading && (
          <SecondaryButton type="button" onClick={() => document.getElementById('video-file-input').click()} className={classes.submitVideoBtn}>
            {t('CAMPAIGN.VIDEO_UPLOADER.BUTTON_UPLOAD_TEXT')}
          </SecondaryButton>
        )}
        {uploading && (
          <div className={classes.uploadingIconWrapper}>
            <FontAwesomeIcon icon={faSpinner} pulse /> <span className={classes.uploadingText}>{t('CAMPAIGN.VIDEO_UPLOADER.UPLOADING')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

VideoInputComponent.defaultProps = {
  campaignId: null,
};

VideoInputComponent.propTypes = {
  uploading: PropTypes.bool.isRequired,
  setUploading: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldError: PropTypes.func.isRequired,
  campaignId: PropTypes.string,
  id: PropTypes.string.isRequired,
  editMode: PropTypes.bool,
};

export default VideoInputComponent;
